<template>
  <div>
    <!-- Unsaved Changes Banner -->
    <div
      v-if="hasUnsavedChanges"
      class="alert alert-secondary p-2 mb-3 alertautosave"
      role="alert"
    >
      <div
        class="d-block align-items-center justify-content-between text-danger"
      >
        <div>
          <div>
            <i
              class="material-icons me-2 text-danger"
              style="vertical-align: middle"
              >warning</i
            >
            <strong>{{ $t("unsavedChanges") }}</strong>
          </div>
          <div v-if="!isNewRecord">
            {{ $t("unsavedChangesMessage") }} ({{ changeCount }})
          </div>
          <div v-if="pendingModifications.length > 0" class="mt-2">
            <details class="modifications-details">
              <summary class="modifications-summary" style="cursor: pointer; font-weight: 500;">
                <i class="fas fa-chevron-down me-1"></i>
                Ver modificaciones pendientes ({{ pendingModifications.length }})
              </summary>
              <div class="mt-2 modifications-list">
                <div 
                  v-for="(mod, index) in pendingModifications" 
                  :key="index"
                  class="modification-item mb-2 p-2 bg-light rounded"
                >
                  <strong class="text-primary">{{ mod.field }}</strong>
                  <div class="d-flex align-items-center mt-1">
                    <span class="text-muted me-2">Antes:</span>
                    <span class="old-value text-decoration-line-through text-muted me-3">
                      {{ formatValue(mod.oldValue) }}
                    </span>
                    <i class="fas fa-arrow-right text-primary me-3"></i>
                    <span class="text-muted me-2">Ahora:</span>
                    <span class="new-value text-success fw-bold">
                      {{ formatValue(mod.newValue) }}
                    </span>
                  </div>
                </div>
              </div>
            </details>
          </div>
          <!-- Alta: no hay autoguardado, el registro lo crea el usuario con «Guardar» -->
          <strong v-if="isNewRecord">{{ $t("newRecordNotSavedYet") }}</strong>
          <template v-else>
            <strong v-if="autoSaveCountdown > 0" class="">
              {{ $t("savingIn") }} {{ autoSaveCountdown }}...
            </strong>
            <div>
              <span v-if="isAutoSaving" class="ms-2">
                - {{ $t("saving") }}...
              </span>
            </div>
            <span v-if="autoSaveError" class="ms-2 text-danger">
              - <i class="fas fa-exclamation-triangle me-1"></i
              >{{ autoSaveErrorMessage }}
            </span>
          </template>
        </div>
        <div v-if="!isNewRecord" class="m-0 mt-2 form-check form-switch">
          <input
            class="form-check-input"
            type="checkbox"
            :id="'disableAutoSave-' + autoSaveDomSuffix"
            v-model="autoSaveDisabled"
            @change="onAutoSaveToggle"
            role="switch"
            :aria-checked="!autoSaveDisabled"
            aria-label="Disable autosave"
          />
          <label class="form-check-label" :for="'disableAutoSave-' + autoSaveDomSuffix">
            <small>{{ $t("disableAutoSave") }}</small>
          </label>
        </div>
      </div>
    </div>

    <!-- Slot para el formulario -->
    <slot></slot>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "AutoSaveForm",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    processFormData: {
      type: Function,
      required: true,
    },
    modelFields: {
      type: Array,
      default: () => [],
    },
    parentID: {
      type: String,
      default: null,
    },
    parentModel: {
      type: String,
      default: null,
    },
    autoSaveDelay: {
      type: Number,
      default: 5000, // 5 segundos por defecto
    },
    baselineDelay: {
      type: Number,
      default: 2000, // 2 segundos para establecer baseline
    },
    disabled: {
      type: Boolean,
      default: false, // Si es true, deshabilita completamente el autosave
    },
    /** El padre está guardando: no arrancar un autoguardado encima. */
    manualSaveInProgress: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /** Evita id/for duplicados cuando hay varios AutoSaveForm en la misma vista (p. ej. FOB proforma + shippings). */
      autoSaveDomSuffix: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      hasUnsavedChanges: false,
      baselineFormData: null,
      baselineProcessedData: null, // Datos procesados del baseline para comparación
      changeCount: 0,
      autoSaveTimer: null,
      autoSaveCountdown: 0,
      isAutoSaving: false,
      autoSaveError: false,
      autoSaveErrorMessage: "",
      autoSaveDisabled: false,
      hasAutoSaveFailed: false, // Bandera para evitar reintentos después de error
      isProcessingError: false, // Bandera para evitar cambios durante procesamiento de error
      isInitialized: false,
      lastSaveTime: 0,
      minSaveInterval: 5000, // Mínimo 5 segundos entre guardados
      isAutomaticChange: false, // Bandera para marcar cambios automáticos (no manuales)
      pendingModifications: [], // Lista de modificaciones pendientes
      pendingModificationsTimer: null, // Timer para debounce de modificaciones
      formInitializationDelay: 3000, // Delay adicional para asegurar que el Form esté completamente inicializado
      formInitialized: false, // Bandera para indicar que el Form está completamente inicializado
      formDataWatcherTimer: null, // Timer para debounce del watcher de formData
      lastFormDataSnapshot: null, // Snapshot del último formData procesado para evitar procesamiento duplicado
      pendingSavePromise: null, // Guardado automático en vuelo (lo espera el guardado manual)
    };
  },
  computed: {
    /**
     * Formulario de alta: todavía no hay documento que actualizar.
     * El autoguardado no da de alta —crearía registros que el usuario no confirmó y, en modelos
     * con autoincrement, les quemaría un correlativo—: avisa que falta guardar y se queda quieto.
     */
    isNewRecord() {
      return !this.formData || !this.formData._id;
    },
  },
  watch: {
    formData: {
      handler(newVal, oldVal) {
        // Si está deshabilitado, no hacer nada
        if (this.disabled) {
          return;
        }
        
        // Ignorar cambios automáticos (no manuales)
        if (this.isAutomaticChange) {
          return;
        }
        
        if (
          !this.isInitialized ||
          !this.formInitialized ||
          this.isAutoSaving ||
          this.isProcessingError
        ) {
          return;
        }

        // Verificación rápida: si las referencias son iguales, no hay cambios
        if (newVal === oldVal) {
          return;
        }

        // Si oldVal es null o undefined, es probablemente la primera carga o inicialización
        // No procesar en estos casos para evitar interferir con la inicialización del Form
        if (!oldVal) {
          return;
        }

        // Limpiar timer anterior si existe (debounce)
        if (this.formDataWatcherTimer) {
          clearTimeout(this.formDataWatcherTimer);
          this.formDataWatcherTimer = null;
        }

        // Crear un snapshot del formData actual para comparación
        const currentSnapshot = JSON.stringify(newVal);

        // Si el snapshot es igual al último procesado, ignorar (evita procesamiento duplicado)
        if (this.lastFormDataSnapshot === currentSnapshot) {
          return;
        }

        // Aplicar debounce para evitar múltiples ejecuciones en rápida sucesión
        this.formDataWatcherTimer = setTimeout(() => {
          // Verificar nuevamente todas las condiciones después del debounce
          if (
            this.isAutomaticChange || 
            this.isAutoSaving || 
            this.isProcessingError ||
            !this.isInitialized ||
            !this.formInitialized ||
            this.disabled
          ) {
            return;
          }

          // Verificar que el snapshot no haya cambiado durante el debounce
          const latestSnapshot = JSON.stringify(this.formData);
          if (this.lastFormDataSnapshot === latestSnapshot) {
            return;
          }

          // Actualizar el snapshot antes de procesar
          this.lastFormDataSnapshot = latestSnapshot;

          // Solo detectar cambios si tenemos baseline y no es la primera carga
          if (this.baselineProcessedData && oldVal) {
            // Usar $nextTick para procesar cambios de forma más inmediata
            // Esto asegura que los cambios en campos normales se detecten correctamente
            this.$nextTick(() => {
              // Verificar nuevamente que no sea un cambio automático
              if (
                this.isAutomaticChange || 
                this.isAutoSaving || 
                this.isProcessingError ||
                !this.isInitialized ||
                !this.formInitialized
              ) {
                return;
              }

              // Comparar los datos procesados (objeto raíz) en lugar de los datos crudos del formulario
              this.hasProcessedDataChanged(this.formData).then((hasChanges) => {
                // Verificar una vez más antes de actualizar
                if (
                  this.isAutomaticChange || 
                  this.isAutoSaving || 
                  this.isProcessingError ||
                  !this.isInitialized ||
                  !this.formInitialized
                ) {
                  return;
                }

                if (this.hasUnsavedChanges !== hasChanges) {
                  this.hasUnsavedChanges = hasChanges;
                  this.$emit("unsaved-changes", hasChanges);
                }

                if (hasChanges) {
                  this.changeCount++;
                        
                  // Calcular modificaciones pendientes con debounce para evitar bucles infinitos
                  this.debounceCalculateModifications(this.formData);

                  // Si había un error anterior y ahora hay un cambio nuevo, resetear la bandera de error
                  if (this.hasAutoSaveFailed) {
                    this.hasAutoSaveFailed = false;
                  }

                  // Solo iniciar auto-save si no está deshabilitado y no ha fallado anteriormente.
                  // En un alta no hay autoguardado: el cartel avisa que falta guardar.
                  if (
                    !this.isNewRecord &&
                    !this.autoSaveDisabled &&
                    !this.hasAutoSaveFailed
                  ) {
                    this.startAutoSaveTimer();
                  }
                } else {
                  this.cancelAutoSave();
                  // Limpiar modificaciones pendientes de forma segura
                  this.$nextTick(() => {
                    this.pendingModifications = [];
                  });
                }
              }).catch((error) => {
                console.error("Error en hasProcessedDataChanged:", error);
              });
            });
          } else {
            // Marcar que hay cambios sin guardar (para casos sin baseline)
            this.$nextTick(() => {
            this.hasUnsavedChanges = true;
            this.$emit("unsaved-changes", true);
            });
          }
        }, 300); // Debounce de 300ms para evitar múltiples ejecuciones
      },
      deep: true,
      immediate: false,
    },
  },
  mounted() {
    // Si está deshabilitado, no inicializar el autosave
    if (this.disabled) {
      return;
    }
    
    // Establecer baseline después del delay especificado + delay adicional para el Form
    setTimeout(async () => {
      // Crear una copia profunda del formData para el baseline
      const baselineCopy = JSON.parse(JSON.stringify(this.formData));
      this.baselineFormData = baselineCopy;
      
      // Crear otra copia para procesar (por si processFormData muta el objeto)
      const processCopy = JSON.parse(JSON.stringify(baselineCopy));
      
      // Procesar los datos del baseline para comparación
      // Manejar tanto funciones síncronas como asíncronas
      const processResult = this.processFormData(processCopy);
      this.baselineProcessedData = processResult instanceof Promise 
        ? await processResult 
        : processResult;
      this.isInitialized = true;
      
      // Marcar que el Form está completamente inicializado después de un delay adicional
      setTimeout(() => {
        this.formInitialized = true;
      }, this.formInitializationDelay);
    }, this.baselineDelay);
  },
  beforeDestroy() {
    // Limpiar timers de auto-guardado
    this.cancelAutoSave();
    // Limpiar timer de modificaciones pendientes
    if (this.pendingModificationsTimer) {
      clearTimeout(this.pendingModificationsTimer);
      this.pendingModificationsTimer = null;
    }
    // Limpiar timer del watcher de formData
    if (this.formDataWatcherTimer) {
      clearTimeout(this.formDataWatcherTimer);
      this.formDataWatcherTimer = null;
    }
  },
  methods: {
    /**
     * Comparar objetos de forma inteligente, ignorando elementos vacíos
     */
    hasRealChanges(oldObj, newObj, visited = new WeakSet()) {
      // Si ambos son null/undefined, no hay cambios
      if ((oldObj === null || oldObj === undefined) && 
          (newObj === null || newObj === undefined)) {
        return false;
      }

      // Si uno es null/undefined y el otro no, verificar si el otro está vacío
      if (oldObj === null || oldObj === undefined) {
        return !this.isValueEmpty(newObj);
      }
      if (newObj === null || newObj === undefined) {
        return !this.isValueEmpty(oldObj);
      }

      // Protección contra referencias circulares
      if (typeof oldObj === "object" && visited.has(oldObj)) {
        return false;
      }
      if (typeof newObj === "object" && visited.has(newObj)) {
        return false;
      }

      if (typeof oldObj === "object") visited.add(oldObj);
      if (typeof newObj === "object") visited.add(newObj);

      // Si ambos están vacíos, no hay cambios
      if (this.isValueEmpty(oldObj) && this.isValueEmpty(newObj)) {
        return false;
      }

      // Si son arrays, usar comparación inteligente
      if (Array.isArray(oldObj) && Array.isArray(newObj)) {
        const arrayComparison = this.compareArrays(oldObj, newObj);
        return arrayComparison !== null; // Si hay comparación, hay cambios reales
      }

      // Si uno es array y el otro no
      if (Array.isArray(oldObj) || Array.isArray(newObj)) {
        const oldIsEmpty = this.isValueEmpty(oldObj);
        const newIsEmpty = this.isValueEmpty(newObj);
        return !(oldIsEmpty && newIsEmpty);
      }

      // Si son objetos, comparar recursivamente
      if (typeof oldObj === "object" && typeof newObj === "object") {
        const allKeys = new Set([
          ...Object.keys(oldObj || {}),
          ...Object.keys(newObj || {})
        ]);

        for (const key of allKeys) {
          const oldValue = oldObj?.[key];
          const newValue = newObj?.[key];
          
          if (this.hasRealChanges(oldValue, newValue, visited)) {
            return true; // Hay al menos un cambio real
          }
        }
        return false; // No hay cambios reales
      }

      // Comparar valores primitivos
      const oldIsEmpty = this.isValueEmpty(oldObj);
      const newIsEmpty = this.isValueEmpty(newObj);
      
      // Si ambos están vacíos, no hay cambios
      if (oldIsEmpty && newIsEmpty) {
        return false;
      }

      // Normalizar valores para comparación
      const normalizedOld = this.normalizeValueForComparison(oldObj);
      const normalizedNew = this.normalizeValueForComparison(newObj);
      
      // Comparar valores normalizados
      return JSON.stringify(normalizedOld) !== JSON.stringify(normalizedNew);
    },

    /**
     * Comparar si los datos procesados (objeto raíz) han cambiado
     * Compara los datos procesados en lugar de los datos crudos del formulario
     */
    async hasProcessedDataChanged(currentFormData) {
      if (!this.baselineProcessedData) return false;

      try {
        // Crear una copia profunda del formData para evitar mutar el original
        // Esto es crítico porque processFormData puede mutar el objeto
        const formDataCopy = JSON.parse(JSON.stringify(currentFormData));
        
        // Procesar los datos actuales del formulario (usando la copia)
        // Manejar tanto funciones síncronas como asíncronas
        const processResult = this.processFormData(formDataCopy);
        const currentProcessedData = processResult instanceof Promise 
          ? await processResult 
          : processResult;
        
        // Limpiar datos para comparación (ignorar campos del sistema)
        const cleanCurrent = this.cleanObjectForComparison(currentProcessedData);
        const cleanBaseline = this.cleanObjectForComparison(this.baselineProcessedData);

        // Usar comparación inteligente que ignora elementos vacíos
        return this.hasRealChanges(cleanBaseline, cleanCurrent);
      } catch (error) {
        console.error("Error al procesar datos para comparación:", error);
        // En caso de error, usar comparación directa como fallback (también con copia)
        const currentCopy = JSON.parse(JSON.stringify(currentFormData));
        const baselineCopy = JSON.parse(JSON.stringify(this.baselineFormData));
        const cleanCurrent = this.cleanObjectForComparison(currentCopy);
        const cleanBaseline = this.cleanObjectForComparison(baselineCopy);
        return this.hasRealChanges(cleanBaseline, cleanCurrent);
      }
    },

    /**
     * Comparar si los datos del formulario han cambiado (método legacy, mantenido por compatibilidad)
     */
    hasFormDataChanged(currentData, originalData) {
      if (!originalData) return false;

      // Limpiar datos para comparación (ignorar campos del sistema)
      const cleanCurrent = this.cleanObjectForComparison(currentData);
      const cleanOriginal = this.cleanObjectForComparison(originalData);

      return JSON.stringify(cleanCurrent) !== JSON.stringify(cleanOriginal);
    },

    /**
     * Limpiar objeto para comparación (ignorar campos del sistema)
     */
    cleanObjectForComparison(obj) {
      if (!obj || typeof obj !== "object") return obj;

      const cleaned = { ...obj };

      // Remover campos del sistema que no deben considerarse para cambios
      delete cleaned._id;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;
      delete cleaned.createdBy;
      delete cleaned.updatedBy;
      delete cleaned.__v;
      delete cleaned.idModel;

      // Limpiar recursivamente objetos anidados
      Object.keys(cleaned).forEach((key) => {
        if (cleaned[key] && typeof cleaned[key] === "object") {
          if (Array.isArray(cleaned[key])) {
            cleaned[key] = cleaned[key].map((item) =>
              typeof item === "object"
                ? this.cleanObjectForComparison(item)
                : item
            );
          } else {
            cleaned[key] = this.cleanObjectForComparison(cleaned[key]);
          }
        }
      });

      return cleaned;
    },

    /**
     * Iniciar timer de auto-guardado
     */
    startAutoSaveTimer() {
      this.cancelAutoSave();

      // Verificar si ha pasado el tiempo mínimo desde el último guardado
      const now = Date.now();
      const timeSinceLastSave = now - this.lastSaveTime;

      if (timeSinceLastSave < this.minSaveInterval) {
        const remainingTime = this.minSaveInterval - timeSinceLastSave;
        setTimeout(() => {
          this.startAutoSaveTimer();
        }, remainingTime);
        return;
      }

      this.autoSaveCountdown = Math.ceil(this.autoSaveDelay / 1000);

      const countdown = () => {
        if (this.autoSaveCountdown > 1) {
          this.autoSaveCountdown--;
          this.autoSaveTimer = setTimeout(countdown, 1000);
        } else {
          this.executeAutoSave();
        }
      };

      this.autoSaveTimer = setTimeout(countdown, 1000);
    },

    /**
     * Cancelar auto-guardado
     */
    cancelAutoSave() {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = null;
      }
      this.autoSaveCountdown = 0;
      this.isAutoSaving = false;
      this.autoSaveError = false;
      this.autoSaveErrorMessage = "";
      // No resetear hasAutoSaveFailed aquí, solo cuando hay un nuevo cambio
    },

    /**
     * Espera al autoguardado en vuelo (si lo hay) antes de que el padre guarde a mano.
     * Sin esto, el guardado manual sale mientras el alta automática todavía no contestó y, como
     * `_id` sigue vacío, pide un alta nueva: dos documentos para el mismo formulario.
     */
    async waitForPendingSave() {
      const pending = this.pendingSavePromise;
      if (!pending) return;
      try {
        await pending;
      } catch (e) {
        /* el error del autoguardado ya se manejó adentro */
      }
    },

    /**
     * Ejecutar auto-guardado
     */
    async executeAutoSave() {
      if (this.isAutoSaving) return;
      // Un alta la confirma el usuario con «Guardar»; acá solo se actualiza lo ya guardado.
      if (this.isNewRecord) {
        this.autoSaveCountdown = 0;
        return;
      }
      // El usuario ya está guardando a mano: ese guardado deja el registro al día.
      if (this.manualSaveInProgress) {
        this.autoSaveCountdown = 0;
        return;
      }

      const run = this.runAutoSave();
      this.pendingSavePromise = run;
      try {
        await run;
      } finally {
        if (this.pendingSavePromise === run) {
          this.pendingSavePromise = null;
        }
      }
    },

    async runAutoSave() {
      this.isAutoSaving = true;
      this.autoSaveCountdown = 0;
      this.autoSaveError = false;
      this.autoSaveErrorMessage = "";

      try {
        // Procesar los datos usando la función proporcionada por el componente padre
        const dataToSave = await this.processFormData(this.formData);
        dataToSave.saveTrigger = "autosave";

        // Agregar parentID y parentModel si están disponibles
        if (this.parentID) {
          dataToSave.parentID = this.parentID;
        }
        if (this.parentModel) {
          dataToSave.parentModel = this.parentModel;
        }

        // Solo actualizar: el alta la hace el usuario con «Guardar» (ver isNewRecord).
        const response = await wencesApi.put(
          `/api/gotrading/dynamic/${this.modelName}/${this.formData._id}`,
          dataToSave
        );

        if (response.status >= 200 && response.status < 300) {
          this.lastSaveTime = Date.now(); // Actualizar timestamp del último guardado
          // Marcar como cambio automático para evitar que los cambios posteriores disparen el watcher
          this.isAutomaticChange = true;
          this.applyServerDerivedValues(
            this.formData,
            response.data,
            this.modelFields
          );
          this.$emit("auto-save-success", response.data);
          this.clearUnsavedChanges();
          // Desactivar la bandera después de que todos los cambios se hayan procesado
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.isAutomaticChange = false;
            });
          });
        } else {
          throw new Error(`Error en la API: ${response.status}`);
        }
      } catch (error) {
        console.error("❌ [AUTO-SAVE] Error:", error);

        // Activar bandera para evitar cambios falsos durante procesamiento de error
        this.isProcessingError = true;

        // Extraer mensaje de error específico de la API
        let errorMessage = "Error al guardar automáticamente.";

        if (error.response && error.response.data) {
          // Si la API devuelve un mensaje de error específico
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (typeof error.response.data === "string") {
            errorMessage = error.response.data;
          }
        } else if (error.message) {
          // Si hay un mensaje de error en el objeto error
          errorMessage = error.message;
        }

        this.autoSaveError = true;
        this.autoSaveErrorMessage = errorMessage;
        this.hasUnsavedChanges = true;
        this.hasAutoSaveFailed = true; // Marcar que el auto-save falló
        this.$emit("auto-save-error", error);

        // Ocultar error después de 5 segundos y desactivar bandera de procesamiento
        setTimeout(() => {
          this.autoSaveError = false;
          this.autoSaveErrorMessage = "";
          this.isProcessingError = false; // Desactivar bandera de procesamiento
        }, 5000);
      } finally {
        this.isAutoSaving = false;
      }
    },

    /**
     * Calcular modificaciones pendientes con debounce para evitar bucles infinitos
     */
    debounceCalculateModifications(currentFormData) {
      // Limpiar timer anterior si existe
      if (this.pendingModificationsTimer) {
        clearTimeout(this.pendingModificationsTimer);
      }

      // Establecer nuevo timer con debounce de 500ms para dar tiempo al Form component
      // Esto evita interferir con operaciones como addArrayItem
      this.pendingModificationsTimer = setTimeout(() => {
        // Verificar una vez más antes de calcular
        if (this.isAutomaticChange || this.isAutoSaving || this.isProcessingError || !this.isInitialized) {
          return;
        }
        this.calculatePendingModifications(currentFormData);
      }, 500);
    },

    /**
     * Calcular las modificaciones pendientes comparando datos procesados
     */
    async calculatePendingModifications(currentFormData) {
      if (!this.baselineProcessedData) {
        // Usar $nextTick para actualizar de forma segura
        this.$nextTick(() => {
          this.pendingModifications = [];
        });
        return;
      }

      try {
        // Crear una copia profunda del formData para evitar mutar el original
        // Esto es crítico porque processFormData puede mutar el objeto
        const formDataCopy = JSON.parse(JSON.stringify(currentFormData));
        
        // Procesar los datos actuales del formulario (usando la copia)
        const processResult = this.processFormData(formDataCopy);
        const currentProcessedData = processResult instanceof Promise 
          ? await processResult 
          : processResult;
        
        // Limpiar datos para comparación
        const cleanCurrent = this.cleanObjectForComparison(currentProcessedData);
        const cleanBaseline = this.cleanObjectForComparison(this.baselineProcessedData);

        // Calcular diferencias con protección contra bucles infinitos
        const differences = this.getObjectDifferences(
          cleanBaseline,
          cleanCurrent,
          "",
          0, // profundidad inicial
          10, // profundidad máxima (ajustable)
          new WeakSet() // objetos visitados para evitar referencias circulares
        );

        // Actualizar de forma segura usando $nextTick para evitar bucles infinitos
        this.$nextTick(() => {
          this.pendingModifications = differences;
        });
      } catch (error) {
        console.error("Error al calcular modificaciones pendientes:", error);
        this.$nextTick(() => {
          this.pendingModifications = [];
        });
      }
    },

    /**
     * Verificar si un valor está realmente vacío (no solo null/undefined)
     */
    isValueEmpty(value) {
      if (value === null || value === undefined) return true;
      if (value === "") return true;
      if (Array.isArray(value)) {
        // Para arrays, verificar si todos los elementos están vacíos
        if (value.length === 0) return true;
        // Verificar si todos los elementos del array están vacíos
        return value.every(item => this.isValueEmpty(item));
      }
      if (typeof value === "object") {
        // Para objetos, verificar si todas las propiedades están vacías
        const keys = Object.keys(value);
        if (keys.length === 0) return true;
        // Verificar si todas las propiedades están vacías (ignorando campos del sistema)
        const systemFields = ['_id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', '__v', 'idModel'];
        const nonSystemKeys = keys.filter(k => !systemFields.includes(k));
        if (nonSystemKeys.length === 0) return true;
        return nonSystemKeys.every(key => this.isValueEmpty(value[key]));
      }
      return false;
    },

    /**
     * Verificar si un elemento de array tiene contenido real
     */
    hasArrayItemContent(item) {
      if (!item || typeof item !== "object") return false;
      const systemFields = ['_id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', '__v', 'idModel'];
      const keys = Object.keys(item).filter(k => !systemFields.includes(k));
      if (keys.length === 0) return false;
      // Verificar si al menos una propiedad no está vacía
      return keys.some(key => !this.isValueEmpty(item[key]));
    },

    /**
     * Normalizar valor para comparación (convertir tipos, fechas, etc.)
     */
    normalizeValueForComparison(value) {
      if (value === null || value === undefined) return value;
      
      // Si es un número, asegurarse de que sea número
      if (typeof value === "number") {
        // Normalizar NaN e Infinity
        if (isNaN(value)) return "NaN";
        if (!isFinite(value)) return value > 0 ? "Infinity" : "-Infinity";
        return value;
      }
      
      // Si es un string que representa un número, convertir a número
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") return "";
        
        // Intentar convertir a número si parece un número
        if (!isNaN(trimmed) && trimmed !== "") {
          const num = Number(trimmed);
          if (!isNaN(num) && isFinite(num)) {
            return num;
          }
        }
        
        // Si parece una fecha, normalizar
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          try {
            const date = new Date(trimmed);
            if (!isNaN(date.getTime())) {
              return date.toISOString();
            }
          } catch (e) {
            // No es una fecha válida, dejar como está
          }
        }
        
        return trimmed;
      }
      
      // Si es una fecha, normalizar
      if (value instanceof Date) {
        return value.toISOString();
      }
      
      return value;
    },

    /**
     * Comparar arrays de forma inteligente, solo considerando cambios reales
     */
    compareArrays(oldArray, newArray) {
      if (!Array.isArray(oldArray) || !Array.isArray(newArray)) {
        return null; // No son arrays, manejar en otro lugar
      }

      // IMPORTANTE: Si la longitud cambió, hay un cambio real (agregar/eliminar items)
      // Esto detecta cuando se agrega un item nuevo, incluso si está vacío
      if (oldArray.length !== newArray.length) {
        return {
          oldCount: oldArray.filter(item => this.hasArrayItemContent(item)).length,
          newCount: newArray.filter(item => this.hasArrayItemContent(item)).length,
          oldTotal: oldArray.length,
          newTotal: newArray.length
        };
      }

      // Filtrar elementos vacíos de ambos arrays para comparación
      const oldNonEmpty = oldArray.filter(item => this.hasArrayItemContent(item));
      const newNonEmpty = newArray.filter(item => this.hasArrayItemContent(item));

      // Si ambos arrays están completamente vacíos, no hay cambio real
      if (oldNonEmpty.length === 0 && newNonEmpty.length === 0) {
        return null; // No hay cambio real
      }

      // Comparar contenido de los elementos no vacíos
      // Limpiar elementos para comparación (remover campos del sistema)
      const cleanOld = oldNonEmpty.map(item => this.cleanObjectForComparison(item));
      const cleanNew = newNonEmpty.map(item => this.cleanObjectForComparison(item));
      
      const oldStr = JSON.stringify(cleanOld);
      const newStr = JSON.stringify(cleanNew);
      
      // Si el contenido es igual, no hay cambio real
      if (oldStr === newStr) {
        return null; // No hay cambio real
      }

      // Hay cambio real, devolver información
      return {
        oldCount: oldNonEmpty.length,
        newCount: newNonEmpty.length,
        oldTotal: oldArray.length,
        newTotal: newArray.length
      };
    },

    /**
     * Obtener diferencias entre dos objetos recursivamente
     * Con protección contra bucles infinitos y límite de profundidad
     */
    getObjectDifferences(
      oldObj, 
      newObj, 
      prefix = "", 
      depth = 0, 
      maxDepth = 10,
      visited = new WeakSet()
    ) {
      const differences = [];

      // Protección contra profundidad excesiva
      if (depth >= maxDepth) {
        if (prefix) {
          differences.push({
            field: this.getFieldLabel(prefix),
            oldValue: "(profundidad máxima alcanzada)",
            newValue: "(profundidad máxima alcanzada)",
          });
        }
        return differences;
      }

      // Protección contra referencias circulares en oldObj
      if (oldObj && typeof oldObj === "object" && !Array.isArray(oldObj)) {
        if (visited.has(oldObj)) {
          // Ya visitado, evitar bucle infinito
          return differences;
        }
        visited.add(oldObj);
      }

      // Protección contra referencias circulares en newObj
      if (newObj && typeof newObj === "object" && !Array.isArray(newObj)) {
        if (visited.has(newObj)) {
          // Ya visitado, evitar bucle infinito
          return differences;
        }
        visited.add(newObj);
      }

      // Si alguno de los objetos es null o undefined, comparar directamente
      if (oldObj === null || oldObj === undefined || 
          newObj === null || newObj === undefined) {
        if (oldObj !== newObj) {
          differences.push({
            field: this.getFieldLabel(prefix),
            oldValue: oldObj,
            newValue: newObj,
          });
        }
        return differences;
      }

      // Obtener todas las claves únicas de ambos objetos
      const allKeys = new Set([
        ...Object.keys(oldObj || {}),
        ...Object.keys(newObj || {})
      ]);

      allKeys.forEach((key) => {
        const fieldName = prefix ? `${prefix}.${key}` : key;
        const oldValue = oldObj?.[key];
        const newValue = newObj?.[key];

        // Si el valor es un objeto (pero no un array ni null)
        if (
          typeof oldValue === "object" &&
          typeof newValue === "object" &&
          !Array.isArray(oldValue) &&
          !Array.isArray(newValue) &&
          oldValue !== null &&
          newValue !== null
        ) {
          // Verificar si ya fue visitado (referencia circular)
          if (visited.has(oldValue) || visited.has(newValue)) {
            // Si es una referencia circular, comparar por identidad
            if (oldValue !== newValue) {
              differences.push({
                field: this.getFieldLabel(fieldName),
                oldValue: "(referencia circular detectada)",
                newValue: "(referencia circular detectada)",
              });
            }
          } else {
            // Recursión para objetos anidados con incremento de profundidad
            const nestedDiffs = this.getObjectDifferences(
              oldValue, 
              newValue, 
              fieldName,
              depth + 1,
              maxDepth,
              visited
            );
            differences.push(...nestedDiffs);
          }
        } else {
          // Comparar valores primitivos o arrays
          // Para arrays, comparar de forma más segura
          if (Array.isArray(oldValue) || Array.isArray(newValue)) {
            // Si ambos son arrays, usar comparación inteligente
            if (Array.isArray(oldValue) && Array.isArray(newValue)) {
              const arrayComparison = this.compareArrays(oldValue, newValue);
              if (arrayComparison) {
                // Solo mostrar si hay cambio real (elementos con contenido)
                differences.push({
                  field: this.getFieldLabel(fieldName),
                  oldValue: `[${arrayComparison.oldCount} elemento${arrayComparison.oldCount !== 1 ? "s" : ""} con contenido]`,
                  newValue: `[${arrayComparison.newCount} elemento${arrayComparison.newCount !== 1 ? "s" : ""} con contenido]`,
                });
              }
            } else {
              // Uno es array y el otro no
              const oldIsEmpty = this.isValueEmpty(oldValue);
              const newIsEmpty = this.isValueEmpty(newValue);
              
              // Solo mostrar si no ambos están vacíos
              if (!(oldIsEmpty && newIsEmpty)) {
                differences.push({
                  field: this.getFieldLabel(fieldName),
                  oldValue: Array.isArray(oldValue) 
                    ? `[${oldValue.length} elemento${oldValue.length !== 1 ? "s" : ""}]`
                    : (oldIsEmpty ? "(vacío)" : oldValue),
                  newValue: Array.isArray(newValue)
                    ? `[${newValue.length} elemento${newValue.length !== 1 ? "s" : ""}]`
                    : (newIsEmpty ? "(vacío)" : newValue),
                });
              }
            }
          } else {
            // Comparar valores primitivos
            const oldIsEmpty = this.isValueEmpty(oldValue);
            const newIsEmpty = this.isValueEmpty(newValue);
            
            // No mostrar diferencia si ambos están vacíos
            if (!(oldIsEmpty && newIsEmpty)) {
              // Normalizar valores para comparación (convertir números, fechas, etc.)
              const normalizedOld = this.normalizeValueForComparison(oldValue);
              const normalizedNew = this.normalizeValueForComparison(newValue);
              
              // Comparación directa primero (más eficiente y precisa)
              if (normalizedOld === normalizedNew) {
                // Valores son iguales, no hay diferencia - continuar con el siguiente campo
                return; // Esto sale de la función callback del forEach
              }
              
              // Si son diferentes tipos pero podrían ser equivalentes
              if (normalizedOld == normalizedNew) {
                // Comparación débil indica que son equivalentes (ej: "65800" == 65800)
                return; // Continuar con el siguiente campo
              }
              
              // Comparar valores normalizados con JSON.stringify como último recurso
              const oldStr = normalizedOld === null || normalizedOld === undefined 
                ? String(normalizedOld) 
                : JSON.stringify(normalizedOld);
              const newStr = normalizedNew === null || normalizedNew === undefined
                ? String(normalizedNew)
                : JSON.stringify(normalizedNew);

              // Solo agregar diferencia si realmente son diferentes
              if (oldStr !== newStr) {
                differences.push({
                  field: this.getFieldLabel(fieldName),
                  oldValue: oldIsEmpty ? "(vacío)" : oldValue,
                  newValue: newIsEmpty ? "(vacío)" : newValue,
                });
              }
            }
          }
        }
      });

      return differences;
    },

    /**
     * Obtener etiqueta legible para un campo
     */
    getFieldLabel(fieldPath) {
      // Intentar encontrar el campo en modelFields para obtener su label
      const pathParts = fieldPath.split(".");
      const fieldKey = pathParts[pathParts.length - 1];
      
      const findField = (fields, key) => {
        for (const field of fields || []) {
          if (field.key === key) {
            return field;
          }
          if (field.fields && Array.isArray(field.fields)) {
            const found = findField(field.fields, key);
            if (found) return found;
          }
        }
        return null;
      };

      const field = findField(this.modelFields, fieldKey);
      if (field && field.label) {
        return field.label;
      }

      // Si no se encuentra, devolver el nombre del campo formateado
      return fieldKey
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    },

    /**
     * Formatear valor para mostrar
     */
    formatValue(value) {
      if (value === null || value === undefined) {
        return "(vacío)";
      }
      if (typeof value === "boolean") {
        return value ? "Sí" : "No";
      }
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return "(vacío)";
        }
        return `[${value.length} elemento${value.length > 1 ? "s" : ""}]`;
      }
      if (typeof value === "object") {
        return "(objeto)";
      }
      if (typeof value === "string" && value.length > 50) {
        return value.substring(0, 50) + "...";
      }
      return String(value);
    },

    /**
     * Limpiar indicadores de cambios
     */
    async clearUnsavedChanges() {
      this.hasUnsavedChanges = false;
      this.changeCount = 0;
      this.pendingModifications = [];
      this.autoSaveError = false;
      this.autoSaveErrorMessage = "";
      this.hasAutoSaveFailed = false; // Resetear bandera de error
      this.isProcessingError = false; // Resetear bandera de procesamiento
      this.cancelAutoSave();

      // Actualizar baseline sin disparar el watcher
      this.isAutomaticChange = true; // Marcar como cambio automático
      this.$nextTick(async () => {
        this.baselineFormData = JSON.parse(JSON.stringify(this.formData));
        // Actualizar también el baseline procesado
        // Manejar tanto funciones síncronas como asíncronas
        const processResult = this.processFormData(this.baselineFormData);
        this.baselineProcessedData = processResult instanceof Promise 
          ? await processResult 
          : processResult;
        // Actualizar el snapshot para evitar procesamiento duplicado
        this.lastFormDataSnapshot = JSON.stringify(this.formData);
        // Desactivar la bandera después de actualizar el baseline
        this.$nextTick(() => {
          this.isAutomaticChange = false;
        });
      });

      this.$emit("unsaved-changes", false);
    },

    /**
     * Método público para limpiar cambios (útil para guardado manual)
     */
    resetChanges() {
      this.clearUnsavedChanges();
    },

    /**
     * Método público para guardado manual (respeta el intervalo mínimo)
     */
    async manualSave() {
      if (this.isAutoSaving) {
        return;
      }

      const now = Date.now();
      const timeSinceLastSave = now - this.lastSaveTime;

      if (timeSinceLastSave < this.minSaveInterval) {
        return;
      }

      await this.executeAutoSave();
    },

    /**
     * ¿El campo quedó vacío en pantalla? Contempla el `{ _id: "", name: "" }` con el que
     * el formulario representa un list/reference sin elegir.
     * @param {*} value
     * @returns {boolean}
     */
    isEmptyFieldValue(value) {
      if (value === null || value === undefined || value === "") return true;
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === "object") return !value._id;
      return false;
    },

    /**
     * Refleja en el formulario los campos que completó el servidor al guardar (p. ej. la
     * cuenta bancaria que sale de la moneda del documento, ticket #397). Sin esto el campo
     * sigue vacío en pantalla hasta recargar, aunque en la base ya tenga valor.
     *
     * Solo toca campos **vacíos**: lo que la persona escribió —incluso mientras viajaba el
     * guardado— nunca se pisa. Los group quedan afuera: los sincroniza el alta manual.
     *
     * @param {Object} formData - Datos del formulario (se mutan)
     * @param {Object} responseData - Documento devuelto por la API
     * @param {Array<Object>} fields - Campos del modelo
     * @returns {boolean} true si algún campo se completó
     */
    applyServerDerivedValues(formData, responseData, fields) {
      if (!formData || !responseData || !Array.isArray(fields)) return false;

      let hasChanges = false;
      fields.forEach((field) => {
        const key = field && field.key;
        if (!key || key === "_id") return;
        if (field.type === "group" || field.type === "file") return;
        if (!Object.prototype.hasOwnProperty.call(responseData, key)) return;
        if (!this.isEmptyFieldValue(formData[key])) return;
        if (this.isEmptyFieldValue(responseData[key])) return;

        this.$set(formData, key, responseData[key]);
        hasChanges = true;
      });

      return hasChanges;
    },

    /**
     * Método público para marcar cambios como automáticos (no manuales)
     * Útil cuando el componente padre necesita hacer cambios programáticos
     * que no deben activar el autosave
     */
    markAsAutomaticChange(callback) {
      this.isAutomaticChange = true;
      if (callback && typeof callback === 'function') {
        callback();
        this.$nextTick(() => {
          this.isAutomaticChange = false;
        });
      } else {
        // Si no hay callback, el componente padre debe llamar a unmarkAsAutomaticChange
        return () => {
          this.$nextTick(() => {
            this.isAutomaticChange = false;
          });
        };
      }
    },

    /**
     * Método público para desmarcar cambios automáticos
     */
    unmarkAsAutomaticChange() {
      this.$nextTick(() => {
        this.isAutomaticChange = false;
      });
    },

    /**
     * Manejar el toggle del switch de desactivar auto-save
     */
    onAutoSaveToggle() {
      if (this.autoSaveDisabled) {
        this.cancelAutoSave();
        this.$emit("auto-save-disabled", true);
      } else {
        this.$emit("auto-save-disabled", false);
        // Si hay cambios pendientes, reiniciar el timer
        if (this.hasUnsavedChanges) {
          this.startAutoSaveTimer();
        }
      }
    },

  },
};
</script>

<style scoped>
/* Estilos para el banner de cambios sin guardar */
.alert-warning {
  border-left: 4px solid #ffc107;
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.alert-warning .material-icons {
  color: #f57c00;
}

/* Estilos para el switch de desactivar auto-save */
.alert-warning .form-switch {
  margin-left: 15px;
}

.alert-warning .form-switch .form-check-input {
  width: 2.5rem;
  height: 1.25rem;
  margin-top: 0.1rem;
  background-color: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 1rem;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
}

.alert-warning .form-switch .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(133, 100, 4, 0.25);
  border-color: #856404;
}

.alert-warning .form-switch .form-check-input:checked {
  background-color: #856404;
  border-color: #856404;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
}

.alert-warning .form-switch .form-check-input:not(:checked) {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23adb5bd'/%3e%3c/svg%3e");
}

.alert-warning .form-switch .form-check-label {
  color: #856404;
  font-weight: 500;
  margin-left: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.alert-warning .form-switch .form-check-label:hover {
  color: #6c5a00;
}
.alertautosave {
  position: fixed;
  right: 0;
  top: 0;
  margin-top: 15px;
  margin-right: 13px;
  background: #ffffffa3;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1050;
}

.modifications-details {
  margin-top: 10px;
}

.modifications-summary {
  color: #856404;
  font-size: 0.9rem;
  user-select: none;
}

.modifications-summary:hover {
  color: #6c5a00;
}

.modifications-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 5px;
}

.modification-item {
  border-left: 3px solid #ffc107;
  font-size: 0.85rem;
}

.modification-item .old-value {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modification-item .new-value {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

details[open] .modifications-summary i {
  transform: rotate(180deg);
  transition: transform 0.2s;
}
</style>
